import { URLS } from '@assets/environment/urls'
import { MessageResponse } from '@entities/AIMessenger/types'
import type { ChatTurnRequestPayload } from '@entities/AIMessenger/model/coachChat'
import { authorizedFetch } from '@shared/api/auth-instance'

export type ChatSessionResponse = {
	id: string
	tenant_id?: string
	user_id: string
	locale: string
	status: string
	title?: string | null
	session_summary?: string | null
	metadata_json?: Record<string, unknown>
	last_message_at?: string | null
	created_at: string
	updated_at: string
	transport?: ChatResponseDiagnostics
}

export type PreviewExercise = {
	exercise_id: string
	name: string
	sets?: number | null
	reps?: string | null
	duration_seconds?: number | null
	rest_seconds?: number | null
	notes?: string | null
}

export type WorkoutPreview = {
	preview_id?: string | null
	revision_no: number
	title: string
	goal: string
	rationale: string
	estimated_duration_min: number
	equipment: string[]
	constraints: string[]
	blocks: Array<{
		name: string
		block_type: 'warmup' | 'main' | 'cooldown'
		exercises: PreviewExercise[]
	}>
	warnings: string[]
}

export type WorkoutPreviewRecord = {
	id: string
	tenant_id?: string
	session_id: string
	user_id?: string
	parent_preview_id?: string | null
	turn_id?: string
	revision_no: number
	status: string
	title: string
	goal: string
	summary_text: string
	preview_json: WorkoutPreview
	preview_hash: string
	safety_state_json?: Record<string, unknown>
	created_workout_id?: string | null
	submit_idempotency_key?: string | null
	approved_at?: string | null
	cancelled_at?: string | null
	expires_at?: string | null
	created_at?: string
	updated_at?: string
}

export type ChatTurnCompletedPayload = {
	session_id: string
	turn_id: string
	message_id: string
	created_at: string
	runtime_status: string
	kind: string
	message?: string
	assistant_text: string
	preview_id?: string | null
	requires_approval: boolean
	preview?: WorkoutPreview | null
	decision?: Record<string, unknown>
	tool_calls?: Array<Record<string, unknown>>
	warnings: string[]
	duration_ms?: number | null
	previewRecord?: WorkoutPreviewRecord | null
	transport?: ChatTransportDiagnostics
}

export type ChatServiceBuildMetadata = {
	version: string | null
	revision: string | null
	buildTime: string | null
}

export type ChatResponseDiagnostics = {
	chatEndpoint: string | null
	service: ChatServiceBuildMetadata | null
}

export type ChatTransportDiagnostics = ChatResponseDiagnostics & {
	requestId: string
}

export type ChatTurnErrorPayload = {
	turn_id?: string
	error: string
}

export class ChatTurnStreamError extends Error {
	public readonly errorCode: string
	public readonly turnId: string | null
	public readonly requestId: string | null
	public readonly chatEndpoint: string | null
	public readonly service: ChatServiceBuildMetadata | null

	constructor(payload: ChatTurnErrorPayload, transport?: ChatTransportDiagnostics) {
		super(payload.error || 'turn_failed')
		this.name = 'ChatTurnStreamError'
		this.errorCode = payload.error || 'turn_failed'
		this.turnId = payload.turn_id || null
		this.requestId = transport?.requestId || null
		this.chatEndpoint = transport?.chatEndpoint || null
		this.service = transport?.service ?? null
	}
}

export type ApprovePreviewResponse = {
	preview_id: string
	status: 'approved'
	workout_id: string
}

export type CancelPreviewResponse = {
	preview_id: string
	status: 'cancelled'
}

export type ChatMessageRecord = {
	id: string
	session_id: string
	turn_id: string
	sequence_no: number
	role: 'user' | 'assistant' | 'system' | 'tool' | 'error'
	message_kind: string
	content_text?: string | null
	content_json?: Record<string, unknown>
	model_name?: string | null
	input_tokens?: number | null
	output_tokens?: number | null
	created_at: string
}

export type ChatMessagesPageResponse = {
	items: ChatMessageRecord[]
	next_before?: number | null
	has_more: boolean
}

type SseEvent = {
	event: string
	data: unknown
}

const authHeaders = (accessToken: string): HeadersInit => ({
	'Authorization': `Bearer ${accessToken}`,
	'Content-Type': 'application/json',
})

const apiUrl = (path: string, baseUrl = URLS.CHAT || ''): string => {
	const base = baseUrl.replace(/\/+$/, '')
	const normalizedPath = path.startsWith('/') ? path : `/${path}`
	return `${base}${normalizedPath}`
}

const chatApiUrls = (path: string): string[] => {
	const primaryUrl = apiUrl(path)
	const fallbackBase = localDevChatFallbackBase()
	if (!fallbackBase) {
		return [primaryUrl]
	}
	const localUrl = apiUrl(path, fallbackBase)
	if (localUrl === primaryUrl) {
		return [primaryUrl]
	}
	return [localUrl, primaryUrl]
}

export class ChatHttpService {
	static getCurrentSession = async (accessToken: string): Promise<ChatSessionResponse> => {
		const result = await requestJsonWithDiagnostics<ChatSessionResponse>(
			'/v1/chat/sessions/current',
			accessToken
		)
		return { ...result.data, transport: result.transport }
	}

	static getSession = async (
		accessToken: string,
		sessionId: string
	): Promise<ChatSessionResponse> => {
		const result = await requestJsonWithDiagnostics<ChatSessionResponse>(
			`/v1/chat/sessions/${sessionId}`,
			accessToken
		)
		return { ...result.data, transport: result.transport }
	}

	static createSession = async (accessToken: string): Promise<ChatSessionResponse> => {
		const result = await requestJsonWithDiagnostics<ChatSessionResponse>(
			'/v1/chat/sessions',
			accessToken,
			{
				method: 'POST',
				body: JSON.stringify({ locale: 'ru-RU' }),
			}
		)
		return { ...result.data, transport: result.transport }
	}

	static getMessagesPage = async ({
		accessToken,
		sessionId,
		beforeSequenceNo,
		limit = 30,
	}: {
		accessToken: string
		sessionId: string
		beforeSequenceNo?: number | null
		limit?: number
	}): Promise<ChatMessagesPageResponse> => {
		const params = new URLSearchParams({ limit: String(limit) })
		if (beforeSequenceNo) {
			params.set('before_sequence_no', String(beforeSequenceNo))
		}
		const response = await requestJson<ChatMessagesPageResponse>(
			`/v1/chat/sessions/${sessionId}/messages?${params.toString()}`,
			accessToken
		)

		if (Array.isArray(response)) {
			return {
				items: response as ChatMessageRecord[],
				next_before: null,
				has_more: false,
			}
		}

		return response
	}

	static sendTurn = async ({
		accessToken,
		sessionId,
		payload,
		requestId = createChatRequestId(),
	}: {
		accessToken: string
		sessionId: string
		payload: ChatTurnRequestPayload
		requestId?: string
	}): Promise<ChatTurnCompletedPayload> => {
		const headers = new Headers(authHeaders(accessToken))
		headers.set('X-Request-ID', requestId)
		headers.set('Idempotency-Key', requestId)
		let response: Response
		let transport: ChatTransportDiagnostics = {
			requestId,
			chatEndpoint: null,
			service: null,
		}
		try {
			const result = await fetchChat(`/v1/chat/sessions/${sessionId}/turns`, {
				method: 'POST',
				headers,
				body: JSON.stringify(payload),
			})
			response = result.response
			transport = {
				requestId,
				...responseDiagnostics(result.response, result.url),
			}
		} catch (error) {
			throw new ChatTurnStreamError(
				{
					error: error instanceof Error ? error.message : 'chat_request_failed',
				},
				transport
			)
		}
		if (!response.ok) {
			throw new ChatTurnStreamError(
				{ error: `chat_turn_failed:${response.status}` },
				transport
			)
		}

		let completed: ChatTurnCompletedPayload | null = null
		let turnError: ChatTurnErrorPayload | null = null
		let startedTurnId: string | null = null
		try {
			for await (const event of readSseEvents(response)) {
				if (event.event === 'turn.started') {
					startedTurnId = stringFromUnknown(
						(event.data as { turn_id?: unknown } | null)?.turn_id
					)
				}
				if (event.event === 'turn.completed') {
					completed = event.data as ChatTurnCompletedPayload
					break
				}
				if (event.event === 'turn.error') {
					turnError = event.data as ChatTurnErrorPayload
					break
				}
			}
		} catch (error) {
			throw new ChatTurnStreamError(
				{
					turn_id: startedTurnId ?? undefined,
					error: error instanceof Error ? error.message : 'chat_stream_failed',
				},
				transport
			)
		}
		if (turnError) {
			throw new ChatTurnStreamError(
				{
					...turnError,
					turn_id: turnError.turn_id ?? startedTurnId ?? undefined,
				},
				transport
			)
		}
		if (!completed) {
			throw new ChatTurnStreamError(
				{
					turn_id: startedTurnId ?? undefined,
					error: 'chat_turn_missing_completion',
				},
				transport
			)
		}
		completed.transport = transport

		const previewId = completed.preview_id || stringFromUnknown(completed.decision?.preview_id)
		if (previewId) {
			try {
				completed.previewRecord = await ChatHttpService.getPreview(accessToken, previewId)
			} catch {
				// turn.completed is the mutation success boundary. Preview hydration is
				// read-only reconciliation and must not expose a duplicate-send retry.
				completed.previewRecord = null
			}
		}
		return completed
	}

	static getPreview = async (
		accessToken: string,
		previewId: string
	): Promise<WorkoutPreviewRecord> => {
		return await requestJson<WorkoutPreviewRecord>(
			`/v1/workout-previews/${previewId}`,
			accessToken
		)
	}

	static approvePreview = async (
		accessToken: string,
		preview: WorkoutPreviewRecord
	): Promise<ApprovePreviewResponse> => {
		return await requestJson<ApprovePreviewResponse>(
			`/v1/workout-previews/${preview.id}/approve`,
			accessToken,
			{
				method: 'POST',
				headers: {
					'Idempotency-Key': `${preview.id}:${preview.revision_no}`,
				},
				body: JSON.stringify({
					revision_no: preview.revision_no,
					preview_hash: preview.preview_hash,
				}),
			}
		)
	}

	static cancelPreview = async (
		accessToken: string,
		previewId: string
	): Promise<CancelPreviewResponse> => {
		return await requestJson<CancelPreviewResponse>(
			`/v1/workout-previews/${previewId}/cancel`,
			accessToken,
			{ method: 'POST' }
		)
	}

	static sendMessage = async (): Promise<boolean | string> => false
	static getMessageResponse = async (): Promise<MessageResponse | ''> => ''
	static getInitialMessages = async (): Promise<MessageResponse[]> => []
	static loadMoreMessages = async (): Promise<MessageResponse[]> => []
}

const requestJson = async <T>(
	path: string,
	accessToken: string,
	init: RequestInit = {}
): Promise<T> => {
	const result = await requestJsonWithDiagnostics<T>(path, accessToken, init)
	return result.data
}

const requestJsonWithDiagnostics = async <T>(
	path: string,
	accessToken: string,
	init: RequestInit = {}
): Promise<{ data: T; transport: ChatResponseDiagnostics }> => {
	const headers = new Headers(authHeaders(accessToken))
	if (init.headers) {
		new Headers(init.headers).forEach((value, key) => headers.set(key, value))
	}
	const { response, url } = await fetchChat(path, { ...init, headers })
	if (!response.ok) {
		throw new Error(`chat_request_failed:${response.status}`)
	}
	return {
		data: (await response.json()) as T,
		transport: responseDiagnostics(response, url),
	}
}

const fetchChat = async (
	path: string,
	init: RequestInit = {}
): Promise<{ response: Response; url: string }> => {
	const urls = chatApiUrls(path)
	let lastError: unknown = null
	for (let index = 0; index < urls.length; index += 1) {
		try {
			const response = await authorizedFetch(urls[index], init)
			if (response.status === 404 && index < urls.length - 1) {
				lastError = new Error('chat_request_failed:404')
				continue
			}
			return { response, url: urls[index] }
		} catch (error) {
			lastError = error
		}
	}
	if (lastError instanceof Error) {
		throw lastError
	}
	throw new Error('chat_request_failed')
}

export const createChatRequestId = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}
	return `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const localDevChatFallbackBase = (): string | null => {
	if (import.meta.env.VITE_CHAT_ENABLE_LOCAL !== 'true') return null
	if (typeof window === 'undefined') return null
	const hostname = window.location.hostname
	if (hostname !== 'localhost' && hostname !== '127.0.0.1') return null
	return 'http://127.0.0.1:8010'
}

async function* readSseEvents(response: Response): AsyncGenerator<SseEvent> {
	if (!response.body) {
		throw new Error('chat_stream_not_supported')
	}
	const reader = response.body.getReader()
	const decoder = new TextDecoder()
	let buffer = ''
	try {
		while (true) {
			const { done, value } = await reader.read()
			if (done) break
			buffer += decoder.decode(value, { stream: true })
			buffer = buffer.replace(/\r\n/g, '\n')
			const chunks = buffer.split('\n\n')
			buffer = chunks.pop() ?? ''
			for (const chunk of chunks) {
				const event = parseSseEvent(chunk)
				if (event) yield event
			}
		}
		buffer += decoder.decode()
		const trailingEvent = parseSseEvent(buffer)
		if (trailingEvent) yield trailingEvent
	} finally {
		await reader.cancel().catch(() => undefined)
		reader.releaseLock()
	}
}

const parseSseEvent = (chunk: string): SseEvent | null => {
	const lines = chunk.replace(/\r\n/g, '\n').split('\n')
	const event = lines
		.find(line => line.startsWith('event:'))
		?.slice('event:'.length)
		.trim()
	const data = lines
		.filter(line => line.startsWith('data:'))
		.map(line => line.slice('data:'.length).trim())
		.join('\n')
	if (!event || !data) return null
	return { event, data: JSON.parse(data) as unknown }
}

const stringFromUnknown = (value: unknown): string | null =>
	typeof value === 'string' && value.trim() ? value : null

const responseDiagnostics = (
	response: Response,
	chatEndpoint: string
): ChatResponseDiagnostics => ({
	chatEndpoint,
	service: serviceBuildMetadata(response.headers),
})

const serviceBuildMetadata = (headers: Headers): ChatServiceBuildMetadata | null => {
	const version = stringFromUnknown(headers.get('Tisit-Service-Version'))
	const revision = stringFromUnknown(headers.get('Tisit-Service-Revision'))
	const buildTime = stringFromUnknown(headers.get('Tisit-Service-Build-Time'))
	if (!version && !revision && !buildTime) return null
	return { version, revision, buildTime }
}
